import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import { Fragment, ReactNode } from "react";

import { Icon } from "../Icon";
import type { Icons } from "../Icon/utils";

type SelectInputPropTypes = {
  disabled?: boolean;
  dropdownPlacement?: "bottom" | "top";
  invalid?: boolean;
  name?: string;
  onChange?: (value: string) => void;
  options: {
    description?: string;
    disabled?: boolean;
    icon?: keyof typeof Icons;
    label: ReactNode;
    value: string;
  }[];
  placeholder?: string;
  required?: boolean;
  selected?: string;
  size?: "normal" | "xs";
  variant?: "default" | "inline";
};

export default function SelectInput({
  options,
  disabled,
  dropdownPlacement = "bottom",
  invalid,
  name,
  onChange,
  placeholder,
  required,
  selected,
  size = "normal",
  variant = "default",
  ...props
}: SelectInputPropTypes) {
  const selectedOption = options.find((opt) => opt.value === selected);

  return (
    <div className="relative w-full">
      <Listbox
        value={selected}
        onChange={onChange}
        disabled={disabled}
        name={name}
        data-testid={`select-input-${name}`}
        {...props}
      >
        {({ open }) => (
          <div
            className={clsx(
              "items-center flex",
              variant === "default" ? "max-w-full w-full" : "w-auto",
            )}
          >
            <ListboxButton
              className={clsx(
                variant === "default"
                  ? "block w-full rounded-lg border px-3 py-2 text-gray-900 shadow-sm text-xs lg:text-sm bg-white"
                  : "inline-flex items-center text-gray-700 hover:text-gray-900 gap-2",
                size === "xs" ? "text-xs lg:text-sm" : "text-sm",
                invalid
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                  : variant === "default"
                    ? "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    : "",
                "focus:outline-none",
                "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
                variant === "default"
                  ? "hover:border-gray-400 transition-colors duration-200"
                  : "",
                "flex items-center justify-between",
                variant === "default" ? "w-full" : "w-auto",
              )}
            >
              <div
                className={clsx(
                  "flex items-center",
                  variant === "default" ? "w-[80%] max-w-[80%]" : "w-auto",
                )}
              >
                {selectedOption && selectedOption.icon && (
                  <Icon
                    name={selectedOption.icon}
                    className={clsx(
                      "mr-2",
                      size === "xs" ? "h-3 w-3" : "h-4 w-4",
                      disabled ? "text-gray-400" : "text-gray-600",
                    )}
                  />
                )}
                <div
                  className={clsx(
                    "flex items-center",
                    variant === "default"
                      ? "flex-col items-start w-full"
                      : "flex-row items-center gap-1 w-auto",
                  )}
                >
                  <span
                    className={clsx(
                      variant === "default"
                        ? "truncate text-left max-w-[calc(100%-10px)] text-ellipsis self-start"
                        : "text-left whitespace-nowrap truncate text-ellipsis",
                      disabled
                        ? "text-gray-400"
                        : selectedOption
                          ? "text-gray-900"
                          : "text-gray-500",
                    )}
                  >
                    {selectedOption ? selectedOption.label : placeholder}
                  </span>
                  {selectedOption && selectedOption.description && (
                    <span
                      className={clsx(
                        "text-gray-500",
                        variant === "default"
                          ? "truncate w-full text-left"
                          : "whitespace-nowrap",
                        size === "xs" ? "text-[10px]" : "text-xs",
                      )}
                    >
                      {selectedOption.description}
                    </span>
                  )}
                </div>
              </div>
              <span className="flex items-center justify-end gap-1 w-[20%]">
                {selectedOption && !disabled && !required && (
                  <span
                    onClick={(e) => {
                      e.preventDefault();
                      onChange?.("");
                    }}
                    className="px-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  >
                    <Icon
                      name="xMark"
                      className={clsx(
                        "text-gray-400 hover:text-gray-600",
                        size === "xs" ? "h-2 w-2" : "h-3 w-3",
                      )}
                    />
                  </span>
                )}
                {!disabled && (
                  <Icon
                    name="chevronDown"
                    className={clsx(
                      "text-gray-400 transition-all duration-200",
                      open && "rotate-180",
                      "hover:text-gray-600",
                      size === "xs" ? "h-2 w-2" : "h-3 w-3",
                    )}
                  />
                )}
                {disabled && (
                  <Icon
                    name="chevronDown"
                    className={clsx(
                      "text-gray-400",
                      size === "xs" ? "h-2 w-2" : "h-3 w-3",
                    )}
                  />
                )}
              </span>
            </ListboxButton>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions
                static
                className={clsx(
                  "absolute z-[60] rounded-lg max-h-56 overflow-auto bg-white py-1",
                  "text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
                  dropdownPlacement === "top"
                    ? "mb-2 bottom-full"
                    : "mt-2 top-full",
                  variant === "default" ? "left-0 right-0" : "right-0",
                  "[&[data-popper-placement^='top']]:bottom-full [&[data-popper-placement^='top']]:mb-2",
                  "[&[data-popper-placement^='bottom']]:top-full [&[data-popper-placement^='bottom']]:mt-2",
                  "[&[data-popper-placement^='left']]:right-full [&[data-popper-placement^='left']]:mr-2",
                  "[&[data-popper-placement^='right']]:left-full [&[data-popper-placement^='right']]:ml-2",
                )}
              >
                {options.map((opt, idx) => (
                  <ListboxOption
                    key={opt.value}
                    value={opt.value}
                    data-testid={`${name}-${idx}`}
                    disabled={opt.value === selected || opt.disabled}
                    className={({ active }) =>
                      clsx(
                        "relative cursor-pointer select-none px-3 py-2 pr-10 flex justify-between items-center w-full",
                        active ? "bg-gray-100" : "text-gray-900",
                        opt.disabled && "cursor-not-allowed opacity-50",
                      )
                    }
                  >
                    {({ selected, active }) => (
                      <>
                        <div
                          className={clsx(
                            "flex items-center",
                            opt.disabled && "text-gray-400",
                            selected ? "w-[calc(90%)]" : "w-[calc(100%)]",
                          )}
                        >
                          {opt.icon && (
                            <Icon
                              name={opt.icon}
                              className={clsx(
                                "mr-2",
                                "h-3 lg:h-4 w-3 lg:w-4",
                                active ? "text-gray-900" : "text-gray-600",
                              )}
                            />
                          )}
                          <div className="flex flex-col w-full">
                            <span
                              className={clsx(
                                "block truncate text-ellipsis w-[calc(100%)] text-xs lg:text-sm",
                                selected ? "font-medium" : "font-normal",
                              )}
                            >
                              {opt.label}
                            </span>
                            {opt.description && (
                              <span className="text-[10px] lg:text-xs text-gray-500 truncate">
                                {opt.description}
                              </span>
                            )}
                          </div>
                        </div>

                        {selected ? (
                          <span
                            className={clsx(
                              "absolute inset-y-0 right-0 flex items-center justify-end pr-3 w-[calc(10%)]",
                              active ? "text-gray-900" : "text-gray-600",
                            )}
                          >
                            <Icon name="circleCheck" className="h-3 w-3" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        )}
      </Listbox>
    </div>
  );
}
